import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BrandLogo from '../BrandLogo';

function Login() {
  const navigate = useNavigate();
  const { login, error: authError } = useAuth();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const ssoErrors = {
    link_required: 'That email belongs to an existing Cloudnotes account. Sign in below, then link Inframodule from Settings.',
    authentication_failed: 'Inframodule sign-in could not be completed. Please try again.',
    verified_email_required: 'A verified email address is required for Inframodule sign-in.',
    login_before_linking: 'Sign in to your existing Cloudnotes account before linking Inframodule.',
    not_configured: 'Inframodule sign-in is not configured yet.',
  };
  const ssoError = ssoErrors[searchParams.get('sso_error')];
  const accessError = searchParams.get('access_unavailable') === '1'
    ? 'This Cloudnotes account is not currently active. Contact support for help.'
    : '';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!formData.username || !formData.password) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    const result = await login(formData.username, formData.password);

    setIsLoading(false);

    if (result.success) {
      navigate('/notes');
    } else {
      setError(result.error || 'Login failed');
    }
  };

  return (
    <>
      <style>{`
        .login-submit-btn {
          transition: all 0.3s ease;
        }
        .login-submit-btn:hover {
          background: #04658f !important;
          box-shadow: 0 0 0 3px rgba(8, 188, 232, 0.25), 0 4px 12px rgba(8, 126, 184, 0.2) !important;
          transform: translateY(-2px);
        }
      `}</style>
      <div style={styles.container}>
        <div style={styles.formCard}>
        <div style={styles.backToHome}>
          <Link to="/" style={styles.backLink}>← Back to Home</Link>
        </div>
        <div style={styles.logo}>
          <BrandLogo />
        </div>
        <p style={styles.subtitle}>Sign in to your account</p>

        {(error || authError || ssoError || accessError) && (
          <div style={styles.errorBox}>
            {error || authError || ssoError || accessError}
          </div>
        )}

        <a href="/api/auth/sso/login" className="login-submit-btn" style={styles.ssoButton}>
          Continue with Inframodule
        </a>
        <div style={styles.divider}><span style={styles.dividerText}>Existing Cloudnotes account</span></div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Username or Email</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter your username or email"
              disabled={isLoading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            className="login-submit-btn"
            style={{
              ...styles.button,
              ...(isLoading ? styles.buttonDisabled : {}),
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.forgotPassword}>
          <Link to="/forgot-password" style={styles.link}>
            Forgot your password?
          </Link>
        </div>

        <p style={styles.footer}>
          New to Inframodule?{' '}
          <a href="/api/auth/sso/login" style={styles.link}>Create one account</a>
        </p>

        <div style={styles.freeNotice}>
          <p style={styles.freeText}>
            One account across Inframodule products
          </p>
          <p style={styles.freeSubtext}>
            Cloudnotes starts free; paid upgrades are optional
          </p>
        </div>
      </div>
    </div>
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f9fc',
    padding: '20px',
  },
  formCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '400px',
    width: '100%',
    border: '1px solid #d9e9f2',
    boxShadow: '0 18px 48px rgba(6, 27, 46, 0.1)',
  },
  backToHome: {
    marginBottom: '16px',
    textAlign: 'center',
  },
  backLink: {
    color: '#04658f',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'opacity 0.2s',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    gap: '10px',
  },
  logoIcon: {
    fontSize: '2.5rem',
  },
  logoText: {
    fontSize: '2rem',
    fontWeight: 'bold',
    background: '#0778ad',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    color: '#666',
    textAlign: 'center',
    marginBottom: '24px',
  },
  errorBox: {
    background: '#fee',
    color: '#c33',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  button: {
    padding: '14px',
    background: '#0778ad',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s',
  },
  ssoButton: {
    display: 'block',
    padding: '14px',
    background: '#061b2e',
    color: 'white',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '700',
    textAlign: 'center',
    textDecoration: 'none',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '22px 0 18px',
    borderTop: '1px solid #d9e9f2',
  },
  dividerText: {
    background: '#fff',
    color: '#52677c',
    fontSize: '12px',
    padding: '0 10px',
    transform: 'translateY(-50%)',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  forgotPassword: {
    textAlign: 'center',
    marginTop: '12px',
  },
  footer: {
    textAlign: 'center',
    marginTop: '20px',
    color: '#666',
    fontSize: '14px',
  },
  link: {
    color: '#04658f',
    textDecoration: 'none',
    fontWeight: '600',
  },
  freeNotice: {
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  freeText: {
    color: '#04658f',
    fontSize: '16px',
    fontWeight: '700',
    margin: '0 0 6px 0',
    letterSpacing: '0.5px',
  },
  freeSubtext: {
    color: '#999',
    fontSize: '12px',
    margin: 0,
    fontWeight: '400',
  },
};

export default Login;
